import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Eye, EyeOff, Camera, CheckCircle2, PartyPopper, Mail, MessageCircle, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/store";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const GoogleBtn = ({ label }) => (
  <button
    type="button"
    data-testid="google-login-btn"
    onClick={googleLogin}
    className="w-full h-12 rounded-full border border-blush-soft bg-white text-sm font-medium flex items-center justify-center gap-2.5"
  >
    <svg className="w-4.5 h-4.5" width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
    {label}
  </button>
);

export default function StoreAuth() {
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [step, setStep] = useState("masuk"); // masuk | daftar | otp-channel | otp | profil | sukses-masuk | sukses-daftar
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "" });
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpInfo, setOtpInfo] = useState(null); // {channel, target, mock, dev_code}
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      setStep("sukses-masuk");
    } catch (err) {
      toast.error(extractError(err));
    } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({ name: form.name, email: form.email, password: form.password, phone: form.phone });
      setStep("otp-channel");
    } catch (err) {
      toast.error(extractError(err));
    } finally { setBusy(false); }
  };

  const sendOtp = async (channel) => {
    const target = channel === "email" ? form.email : form.phone;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/send", { channel, target });
      setOtpInfo(data);
      setOtp(["", "", "", "", "", ""]);
      setStep("otp");
      if (data.mock) toast.info(`Mode mock — kode: ${data.dev_code}`);
      else toast.success(`Kode OTP dikirim ke ${target}`);
    } catch (err) {
      toast.error(extractError(err));
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (otp.join("").length < 6) return toast.error("Masukkan 6 digit kode OTP");
    setBusy(true);
    try {
      await api.post("/auth/otp/verify", { target: otpInfo.target, code: otp.join("") });
      toast.success("Verifikasi berhasil!");
      setStep("profil");
    } catch (err) {
      toast.error(extractError(err));
    } finally { setBusy(false); }
  };

  const Head = ({ title, sub }) => (
    <div className="text-center mb-6">
      <div className="font-serif-fashion text-3xl tracking-[0.25em] text-cocoa">KIYANA</div>
      <div className="text-[10px] tracking-[0.2em] text-cocoa-muted mb-5">by Cirebon Cloth</div>
      {title && <div className="font-semibold text-lg">{title}</div>}
      {sub && <div className="text-xs text-cocoa-muted mt-1">{sub}</div>}
    </div>
  );

  const backMap = { daftar: "masuk", "otp-channel": "daftar", otp: "otp-channel", profil: "otp-channel" };

  return (
    <div className="min-h-screen bg-canvas-soft px-5 py-6 max-w-md mx-auto">
      {backMap[step] && (
        <button onClick={() => setStep(backMap[step])} className="w-9 h-9 rounded-full bg-white border border-blush-soft flex items-center justify-center mb-4">
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}

      {step === "masuk" && (
        <div className="animate-fade-up">
          <Head title="Masuk" sub="Selamat datang kembali! Yuk masuk ke akunmu 💕" />
          <form onSubmit={doLogin} className="space-y-3">
            <div>
              <div className="text-xs font-medium mb-1.5">Email</div>
              <input data-testid="store-login-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Masukkan email kamu" required className="w-full h-12 px-4 rounded-2xl border border-blush-soft bg-white text-sm focus:outline-none focus:ring-2 ring-brand" />
            </div>
            <div>
              <div className="text-xs font-medium mb-1.5">Kata Sandi</div>
              <div className="relative">
                <input data-testid="store-login-password" type={showPw ? "text" : "password"} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Masukkan kata sandi" required className="w-full h-12 px-4 pr-11 rounded-2xl border border-blush-soft bg-white text-sm focus:outline-none focus:ring-2 ring-brand" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-cocoa-muted">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-right mt-1.5"><span className="text-[11px] text-brand">Lupa kata sandi?</span></div>
            </div>
            <button data-testid="store-login-submit" disabled={busy} className="w-full h-12 rounded-full bg-brand text-white font-semibold text-sm disabled:opacity-50">
              {busy ? "Memproses..." : "Masuk"}
            </button>
          </form>
          <div className="text-center text-xs text-cocoa-muted my-5">atau masuk dengan</div>
          <GoogleBtn label="Masuk dengan Google" />
          <div className="text-center text-xs text-cocoa-muted mt-6">
            Belum punya akun? <button onClick={() => setStep("daftar")} className="text-brand font-semibold">Daftar sekarang</button>
          </div>
          <div className="text-center text-[11px] text-cocoa-muted mt-3">
            Belanja tanpa akun? <Link to="/store" className="text-brand">Lanjut sebagai tamu</Link>
          </div>
        </div>
      )}

      {step === "daftar" && (
        <div className="animate-fade-up">
          <Head title="Daftar Akun" sub="Yuk daftar akun baru untuk pengalaman belanja yang lebih seru! 💕" />
          <form onSubmit={doRegister} className="space-y-3">
            <div>
              <div className="text-xs font-medium mb-1.5">Nama Lengkap</div>
              <input data-testid="store-reg-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Masukkan nama lengkap" required className="w-full h-12 px-4 rounded-2xl border border-blush-soft bg-white text-sm focus:outline-none focus:ring-2 ring-brand" />
            </div>
            <div>
              <div className="text-xs font-medium mb-1.5">No. HP / WhatsApp</div>
              <input data-testid="store-reg-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="08xx xxxx xxxx" required className="w-full h-12 px-4 rounded-2xl border border-blush-soft bg-white text-sm focus:outline-none focus:ring-2 ring-brand" />
            </div>
            <div>
              <div className="text-xs font-medium mb-1.5">Email</div>
              <input data-testid="store-reg-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Masukkan email kamu" required className="w-full h-12 px-4 rounded-2xl border border-blush-soft bg-white text-sm focus:outline-none focus:ring-2 ring-brand" />
            </div>
            <div>
              <div className="text-xs font-medium mb-1.5">Kata Sandi</div>
              <input data-testid="store-reg-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Buat kata sandi (min. 6 karakter)" required minLength={6} className="w-full h-12 px-4 rounded-2xl border border-blush-soft bg-white text-sm focus:outline-none focus:ring-2 ring-brand" />
            </div>
            <button data-testid="store-reg-submit" disabled={busy} className="w-full h-12 rounded-full bg-brand text-white font-semibold text-sm disabled:opacity-50">
              {busy ? "Memproses..." : "Daftar"}
            </button>
          </form>
          <div className="text-center text-xs text-cocoa-muted my-5">atau daftar dengan</div>
          <GoogleBtn label="Daftar dengan Google" />
          <div className="text-center text-xs text-cocoa-muted mt-5">
            Sudah punya akun? <button onClick={() => setStep("masuk")} className="text-brand font-semibold">Masuk di sini</button>
          </div>
        </div>
      )}

      {step === "otp-channel" && (
        <div className="animate-fade-up">
          <Head title="Verifikasi Akun" sub="Pilih cara menerima kode OTP (opsional — bisa dilewati)" />
          <div className="space-y-3">
            <button data-testid="otp-channel-email" onClick={() => sendOtp("email")} disabled={busy} className="w-full p-4 rounded-2xl border border-blush-soft bg-white flex items-center gap-3 text-left">
              <div className="w-11 h-11 rounded-xl bg-blush flex items-center justify-center"><Mail className="w-5 h-5 text-brand" /></div>
              <div className="flex-1">
                <div className="text-sm font-semibold">Email</div>
                <div className="text-xs text-cocoa-muted">{form.email}</div>
              </div>
            </button>
            <button data-testid="otp-channel-wa" onClick={() => sendOtp("whatsapp")} disabled={busy} className="w-full p-4 rounded-2xl border border-blush-soft bg-white flex items-center gap-3 text-left">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-emerald-600" /></div>
              <div className="flex-1">
                <div className="text-sm font-semibold">WhatsApp</div>
                <div className="text-xs text-cocoa-muted">{form.phone}</div>
              </div>
            </button>
            {busy && <div className="text-center"><Loader2 className="w-5 h-5 animate-spin text-brand mx-auto" /></div>}
            <button data-testid="otp-skip" onClick={() => setStep("profil")} className="w-full h-11 rounded-full text-sm text-cocoa-muted">
              Lewati dulu, nanti saja →
            </button>
          </div>
        </div>
      )}

      {step === "otp" && (
        <div className="animate-fade-up">
          <Head title="Verifikasi OTP" sub={`Kode OTP telah dikirim ke ${otpInfo?.target || ""} via ${otpInfo?.channel === "email" ? "Email" : "WhatsApp"}`} />
          <div className="flex justify-center gap-2.5 my-6">
            {otp.map((d, i) => (
              <input
                key={i}
                data-testid={`otp-${i}`}
                value={d}
                inputMode="numeric"
                maxLength={1}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  const next = [...otp];
                  next[i] = v;
                  setOtp(next);
                  if (v && e.target.nextSibling) e.target.nextSibling.focus();
                }}
                className="w-11 py-3 text-center text-lg font-bold rounded-xl border border-blush-soft bg-white focus:outline-none focus:ring-2 ring-brand"
              />
            ))}
          </div>
          {otpInfo?.mock && (
            <div className="text-center text-[11px] bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-4 text-amber-700">
              Mode mock (SMTP/WA belum dikonfigurasi) — kode: <b className="font-mono">{otpInfo.dev_code}</b>
            </div>
          )}
          <button data-testid="otp-verify" onClick={verifyOtp} disabled={busy} className="w-full h-12 rounded-full bg-brand text-white font-semibold text-sm disabled:opacity-50">
            {busy ? "Memverifikasi..." : "Verifikasi"}
          </button>
          <button data-testid="otp-resend" onClick={() => sendOtp(otpInfo.channel)} disabled={busy} className="w-full h-12 rounded-full border border-blush-soft text-sm font-medium mt-2 bg-white">Kirim ulang kode</button>
          <button onClick={() => setStep("profil")} className="w-full h-11 text-sm text-cocoa-muted mt-1">Lewati →</button>
        </div>
      )}

      {step === "profil" && (
        <div className="animate-fade-up">
          <Head title="Lengkapi Profil" sub="Yay! Tinggal sedikit lagi 🎉 Lengkapi profilmu yuk" />
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-full bg-blush border-2 border-dashed border-brand flex flex-col items-center justify-center text-brand">
              <Camera className="w-6 h-6" />
              <span className="text-[9px] mt-1">Tambah Foto</span>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-medium mb-1.5">Tanggal Lahir (opsional)</div>
              <input type="date" className="w-full h-12 px-4 rounded-2xl border border-blush-soft bg-white text-sm focus:outline-none focus:ring-2 ring-brand" />
            </div>
            <div>
              <div className="text-xs font-medium mb-1.5">Jenis Kelamin (opsional)</div>
              <div className="flex gap-2">
                {["Perempuan", "Laki-laki"].map((g, i) => (
                  <button key={g} type="button" className={`flex-1 h-11 rounded-full border text-sm ${i === 0 ? "border-brand bg-blush text-brand font-medium" : "border-blush-soft bg-white"}`}>{g}</button>
                ))}
              </div>
            </div>
          </div>
          <button data-testid="profil-save" onClick={() => setStep("sukses-daftar")} className="w-full h-12 rounded-full bg-brand text-white font-semibold text-sm mt-6">Simpan & Lanjut</button>
        </div>
      )}

      {step === "sukses-masuk" && (
        <div className="text-center py-16 animate-fade-up">
          <div className="w-24 h-24 rounded-full bg-blush mx-auto flex items-center justify-center mb-5">
            <CheckCircle2 className="w-12 h-12 text-brand" />
          </div>
          <div className="font-serif-fashion text-2xl text-cocoa mb-1">Berhasil Masuk! 🎉</div>
          <div className="text-sm text-cocoa-muted mb-8">Selamat datang di Kiyana</div>
          <button data-testid="sukses-belanja" onClick={() => nav("/store")} className="w-full h-12 rounded-full bg-brand text-white font-semibold text-sm">Mulai Belanja</button>
        </div>
      )}

      {step === "sukses-daftar" && (
        <div className="text-center py-14 animate-fade-up">
          <PartyPopper className="w-14 h-14 text-brand mx-auto mb-4" />
          <div className="font-serif-fashion text-2xl text-cocoa mb-1">Selamat Datang di Kiyana! 🎀</div>
          <div className="text-sm text-cocoa-muted mb-6">Akun kamu berhasil dibuat</div>
          <div className="text-left space-y-2 mb-8 max-w-xs mx-auto">
            {["Dapatkan promo & voucher eksklusif", "Pantau pesanan dengan mudah", "Simpan wishlist favoritmu"].map((b) => (
              <div key={b} className="flex items-center gap-2 text-xs text-cocoa">
                <CheckCircle2 className="w-4 h-4 text-brand flex-shrink-0" /> {b}
              </div>
            ))}
          </div>
          <button data-testid="sukses-daftar-belanja" onClick={() => nav("/store")} className="w-full h-12 rounded-full bg-brand text-white font-semibold text-sm">Mulai Belanja</button>
        </div>
      )}
    </div>
  );
}
